// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import * as ExcelJS from "exceljs";

export type DailyForecast = { nights: number; people: number; occupancyRate: number };
export type ComparisonData = {
  availableNights: { current: number; previous: number };
  occupiedNights: { current: number; previous: number };
  persons: { current: number; previous: number };
  averageOccupancy: { current: number; previous: number };
};

function formatDateExcelLabel(date: Date): string {
  const day = date.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${day}-${months[date.getMonth()]}`;
}

function sum(arr: number[]) { 
  return arr.reduce((a, b) => a + b, 0); 
}

async function fetchTemplate(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Template introuvable à ${url}`);
  return await res.arrayBuffer();
}

export async function exportGrillesInfoXlsxUsingTemplate(params: {
  templateUrl?: string;
  startDate: Date;
  weeklyForecastData: DailyForecast[];
  comparisonWeek1?: ComparisonData | null;
  comparisonWeek2?: ComparisonData | null;
  hotelNames?: string[];
}) {
  const { templateUrl = "/GRILLES_INFO_TEMPLATE.xlsx", startDate, weeklyForecastData, comparisonWeek1, comparisonWeek2, hotelNames = [] } = params;

  const buffer = await fetchTemplate(templateUrl).catch(async (e) => {
    return await fetchTemplate("GRILLES_INFO_TEMPLATE.xlsx");
  });
  
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets.find((ws: any) => 
    ws.name.toLowerCase().includes("prévision") || ws.name.toLowerCase().includes("prevision")
  ) || workbook.worksheets[0];

  const week1 = weeklyForecastData.slice(0, 7);
  const week2 = weeklyForecastData.slice(7, 14);
  const dayKeys = ["FRI", "SAT", "SUN", "MON", "TUE", "WED", "THU"] as const;

  const cellMappingPrevision: Record<string, string> = {
    "W1_AVG_AVAILABLE": "C8",
    "W1_COMP_AVAIL_CURR": "G10",
    "W1_COMP_AVAIL_DELTA": "H10",
    "W1_COMP_AVAIL_PREV": "F10",
    "W1_COMP_OCCUP_CURR": "G11",
    "W1_COMP_OCCUP_DELTA": "H11",
    "W1_COMP_OCCUP_PREV": "F11",
    "W1_COMP_PERS_CURR": "G12",
    "W1_COMP_PERS_DELTA": "H12",
    "W1_COMP_PERS_PREV": "F12",
    "W1_COMP_RATE_CURR": "G13",
    "W1_COMP_RATE_DELTA": "H13",
    "W1_COMP_RATE_PREV": "F13",
    "W1_COMP_YEAR_CURR": "G8",
    "W1_COMP_YEAR_PREV": "F8",
    "W1_DATES_FRI": "C4",
    "W1_DATES_MON": "F4",
    "W1_DATES_SAT": "D4",
    "W1_DATES_SUN": "E4",
    "W1_DATES_THU": "I4",
    "W1_DATES_TUE": "G4",
    "W1_DATES_WED": "H4",
    "W1_NIGHTS_FRI": "C4",
    "W1_NIGHTS_MON": "F4",
    "W1_NIGHTS_SAT": "D4",
    "W1_NIGHTS_SUN": "E4",
    "W1_NIGHTS_THU": "I4",
    "W1_NIGHTS_TOTAL": "J4",
    "W1_NIGHTS_TUE": "G4",
    "W1_NIGHTS_WED": "H4",
    "W1_OCC_AVG": "J6",
    "W1_OCC_FRI": "C6",
    "W1_OCC_MON": "F6",
    "W1_OCC_SAT": "D6",
    "W1_OCC_SUN": "E6",
    "W1_OCC_THU": "I6",
    "W1_OCC_TUE": "G6",
    "W1_OCC_WED": "H6",
    "W1_PERSONS_FRI": "C5",
    "W1_PERSONS_MON": "F5",
    "W1_PERSONS_SAT": "D5",
    "W1_PERSONS_SUN": "E5",
    "W1_PERSONS_THU": "I5",
    "W1_PERSONS_TOTAL": "J5",
    "W1_PERSONS_TUE": "G5",
    "W1_PERSONS_WED": "H5",
    "W1_RATIO_USED": "C7",
    "W2_AVG_AVAILABLE": "C21",
    "W2_COMP_AVAIL_CURR": "G23",
    "W2_COMP_AVAIL_DELTA": "H23",
    "W2_COMP_AVAIL_PREV": "F23",
    "W2_COMP_OCCUP_CURR": "G24",
    "W2_COMP_OCCUP_DELTA": "H24",
    "W2_COMP_OCCUP_PREV": "F24",
    "W2_COMP_PERS_CURR": "G25",
    "W2_COMP_PERS_DELTA": "H25",
    "W2_COMP_PERS_PREV": "F25",
    "W2_COMP_RATE_CURR": "G26",
    "W2_COMP_RATE_DELTA": "H26",
    "W2_COMP_RATE_PREV": "F26",
    "W2_COMP_YEAR_CURR": "G21",
    "W2_COMP_YEAR_PREV": "F21",
    "W2_DATES_FRI": "C17",
    "W2_DATES_MON": "F17",
    "W2_DATES_SAT": "D17",
    "W2_DATES_SUN": "E17",
    "W2_DATES_THU": "I17",
    "W2_DATES_TUE": "G17",
    "W2_DATES_WED": "H17",
    "W2_NIGHTS_FRI": "C17",
    "W2_NIGHTS_MON": "F17",
    "W2_NIGHTS_SAT": "D17",
    "W2_NIGHTS_SUN": "E17",
    "W2_NIGHTS_THU": "I17",
    "W2_NIGHTS_TOTAL": "J17",
    "W2_NIGHTS_TUE": "G17",
    "W2_NIGHTS_WED": "H17",
    "W2_OCC_AVG": "J19",
    "W2_OCC_FRI": "C19",
    "W2_OCC_MON": "F19",
    "W2_OCC_SAT": "D19",
    "W2_OCC_SUN": "E19",
    "W2_OCC_THU": "I19",
    "W2_OCC_TUE": "G19",
    "W2_OCC_WED": "H19",
    "W2_PERSONS_FRI": "C18",
    "W2_PERSONS_MON": "F18",
    "W2_PERSONS_SAT": "D18",
    "W2_PERSONS_SUN": "E18",
    "W2_PERSONS_THU": "I18",
    "W2_PERSONS_TOTAL": "J18",
    "W2_PERSONS_TUE": "G18",
    "W2_PERSONS_WED": "H18",
    "W2_RATIO_USED": "C20",
  };

  // Helper to write to named cell using mapping
  function writeToNamedCell(name: string, value: any): boolean {
    try {
      const cellAddress = cellMappingPrevision[name];
      if (!cellAddress) return false;

      const cell = worksheet.getCell(cellAddress);
      const existingStyle = {
        font: cell.font ? { ...cell.font } : undefined,
        alignment: cell.alignment ? { ...cell.alignment } : undefined,
        border: cell.border ? { ...cell.border } : undefined,
        fill: cell.fill ? { ...cell.fill } : undefined,
        numFmt: cell.numFmt,
      };
      
      cell.value = value;
      
      if (existingStyle.font) cell.font = existingStyle.font;
      if (existingStyle.alignment) cell.alignment = existingStyle.alignment;
      if (existingStyle.border) cell.border = existingStyle.border;
      if (existingStyle.fill) cell.fill = existingStyle.fill;
      if (existingStyle.numFmt) cell.numFmt = existingStyle.numFmt;
      
      return true;
    } catch (e) {
      return false;
    }
  }

  // Week 1 - Dates, nights, persons, occupancy
  dayKeys.forEach((dk, idx) => {
    const offset = idx;
    const dateLabel = formatDateExcelLabel(new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + offset));
    const d = week1[idx] || { nights: 0, people: 0, occupancyRate: 0 };
    
    writeToNamedCell(`W1_DATES_${dk}`, dateLabel);
    writeToNamedCell(`W1_NIGHTS_${dk}`, d.nights || 0);
    writeToNamedCell(`W1_PERSONS_${dk}`, d.people || 0);
    writeToNamedCell(`W1_OCC_${dk}`, `${Number(d.occupancyRate || 0).toFixed(2)}%`);
  });

  // Week 1 - Totals and metrics
  const w1Nights = week1.map(d => d.nights || 0);
  const w1People = week1.map(d => d.people || 0);
  const w1Occ = week1.map(d => d.occupancyRate || 0);
  
  writeToNamedCell("W1_NIGHTS_TOTAL", sum(w1Nights));
  writeToNamedCell("W1_PERSONS_TOTAL", sum(w1People));
  writeToNamedCell("W1_OCC_AVG", `${(sum(w1Occ)/7).toFixed(2)}%`);
  
  const ratio1 = sum(w1Nights) > 0 ? (sum(w1People) / sum(w1Nights)) : 0;
  writeToNamedCell("W1_RATIO_USED", Number(ratio1.toFixed(2)));
  
  const availList1 = week1.map(d => d.occupancyRate > 0 ? Math.round(d.nights * 100 / d.occupancyRate) : 0);
  const avgAvail1 = availList1.length ? Math.round(sum(availList1) / availList1.length) : 0;
  writeToNamedCell("W1_AVG_AVAILABLE", avgAvail1);

  // Week 1 - Comparison
  if (comparisonWeek1) {
    const currentYear = startDate.getFullYear();
    const previousYear = currentYear - 1;
    
    writeToNamedCell("W1_COMP_YEAR_PREV", previousYear);
    writeToNamedCell("W1_COMP_YEAR_CURR", currentYear);
    
    writeToNamedCell("W1_COMP_AVAIL_PREV", comparisonWeek1.availableNights.previous);
    writeToNamedCell("W1_COMP_AVAIL_CURR", comparisonWeek1.availableNights.current);
    const deltaAvail1 = comparisonWeek1.availableNights.previous === 0 ? "—" : 
      `${(((comparisonWeek1.availableNights.current - comparisonWeek1.availableNights.previous) / comparisonWeek1.availableNights.previous) * 100).toFixed(1)}%`;
    writeToNamedCell("W1_COMP_AVAIL_DELTA", deltaAvail1);
    
    writeToNamedCell("W1_COMP_OCCUP_PREV", comparisonWeek1.occupiedNights.previous);
    writeToNamedCell("W1_COMP_OCCUP_CURR", comparisonWeek1.occupiedNights.current);
    const deltaOccup1 = comparisonWeek1.occupiedNights.previous === 0 ? "—" : 
      `${(((comparisonWeek1.occupiedNights.current - comparisonWeek1.occupiedNights.previous) / comparisonWeek1.occupiedNights.previous) * 100).toFixed(1)}%`;
    writeToNamedCell("W1_COMP_OCCUP_DELTA", deltaOccup1);
    
    writeToNamedCell("W1_COMP_PERS_PREV", comparisonWeek1.persons.previous);
    writeToNamedCell("W1_COMP_PERS_CURR", comparisonWeek1.persons.current);
    const deltaPers1 = comparisonWeek1.persons.previous === 0 ? "—" : 
      `${(((comparisonWeek1.persons.current - comparisonWeek1.persons.previous) / comparisonWeek1.persons.previous) * 100).toFixed(1)}%`;
    writeToNamedCell("W1_COMP_PERS_DELTA", deltaPers1);
    
    writeToNamedCell("W1_COMP_RATE_PREV", `${comparisonWeek1.averageOccupancy.previous}%`);
    writeToNamedCell("W1_COMP_RATE_CURR", `${comparisonWeek1.averageOccupancy.current}%`);
    const deltaRate1 = comparisonWeek1.averageOccupancy.previous === 0 ? "—" : 
      `${(((comparisonWeek1.averageOccupancy.current - comparisonWeek1.averageOccupancy.previous) / comparisonWeek1.averageOccupancy.previous) * 100).toFixed(1)}%`;
    writeToNamedCell("W1_COMP_RATE_DELTA", deltaRate1);
  }

  // Week 2 - Dates, nights, persons, occupancy
  dayKeys.forEach((dk, idx) => {
    const offset = 7 + idx;
    const dateLabel = formatDateExcelLabel(new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + offset));
    const d = week2[idx] || { nights: 0, people: 0, occupancyRate: 0 };
    
    writeToNamedCell(`W2_DATES_${dk}`, dateLabel);
    writeToNamedCell(`W2_NIGHTS_${dk}`, d.nights || 0);
    writeToNamedCell(`W2_PERSONS_${dk}`, d.people || 0);
    writeToNamedCell(`W2_OCC_${dk}`, `${Number(d.occupancyRate || 0).toFixed(2)}%`);
  });

  // Week 2 - Totals and metrics
  const w2Nights = week2.map(d => d.nights || 0);
  const w2People = week2.map(d => d.people || 0);
  const w2Occ = week2.map(d => d.occupancyRate || 0);
  
  writeToNamedCell("W2_NIGHTS_TOTAL", sum(w2Nights));
  writeToNamedCell("W2_PERSONS_TOTAL", sum(w2People));
  writeToNamedCell("W2_OCC_AVG", `${(sum(w2Occ)/7).toFixed(2)}%`);
  
  const ratio2 = sum(w2Nights) > 0 ? (sum(w2People) / sum(w2Nights)) : 0;
  writeToNamedCell("W2_RATIO_USED", Number(ratio2.toFixed(2)));
  
  const availList2 = week2.map(d => d.occupancyRate > 0 ? Math.round(d.nights * 100 / d.occupancyRate) : 0);
  const avgAvail2 = availList2.length ? Math.round(sum(availList2) / availList2.length) : 0;
  writeToNamedCell("W2_AVG_AVAILABLE", avgAvail2);

  // Week 2 - Comparison
  if (comparisonWeek2) {
    const currentYear = startDate.getFullYear();
    const previousYear = currentYear - 1;
    
    writeToNamedCell("W2_COMP_YEAR_PREV", previousYear);
    writeToNamedCell("W2_COMP_YEAR_CURR", currentYear);
    
    writeToNamedCell("W2_COMP_AVAIL_PREV", comparisonWeek2.availableNights.previous);
    writeToNamedCell("W2_COMP_AVAIL_CURR", comparisonWeek2.availableNights.current);
    const deltaAvail2 = comparisonWeek2.availableNights.previous === 0 ? "—" : 
      `${(((comparisonWeek2.availableNights.current - comparisonWeek2.availableNights.previous) / comparisonWeek2.availableNights.previous) * 100).toFixed(1)}%`;
    writeToNamedCell("W2_COMP_AVAIL_DELTA", deltaAvail2);
    
    writeToNamedCell("W2_COMP_OCCUP_PREV", comparisonWeek2.occupiedNights.previous);
    writeToNamedCell("W2_COMP_OCCUP_CURR", comparisonWeek2.occupiedNights.current);
    const deltaOccup2 = comparisonWeek2.occupiedNights.previous === 0 ? "—" : 
      `${(((comparisonWeek2.occupiedNights.current - comparisonWeek2.occupiedNights.previous) / comparisonWeek2.occupiedNights.previous) * 100).toFixed(1)}%`;
    writeToNamedCell("W2_COMP_OCCUP_DELTA", deltaOccup2);
    
    writeToNamedCell("W2_COMP_PERS_PREV", comparisonWeek2.persons.previous);
    writeToNamedCell("W2_COMP_PERS_CURR", comparisonWeek2.persons.current);
    const deltaPers2 = comparisonWeek2.persons.previous === 0 ? "—" : 
      `${(((comparisonWeek2.persons.current - comparisonWeek2.persons.previous) / comparisonWeek2.persons.previous) * 100).toFixed(1)}%`;
    writeToNamedCell("W2_COMP_PERS_DELTA", deltaPers2);
    
    writeToNamedCell("W2_COMP_RATE_PREV", `${comparisonWeek2.averageOccupancy.previous}%`);
    writeToNamedCell("W2_COMP_RATE_CURR", `${comparisonWeek2.averageOccupancy.current}%`);
    const deltaRate2 = comparisonWeek2.averageOccupancy.previous === 0 ? "—" : 
      `${(((comparisonWeek2.averageOccupancy.current - comparisonWeek2.averageOccupancy.previous) / comparisonWeek2.averageOccupancy.previous) * 100).toFixed(1)}%`;
    writeToNamedCell("W2_COMP_RATE_DELTA", deltaRate2);
  }

  const currentYear = startDate.getFullYear();
  const previousYear = currentYear - 1;
  
  worksheet.eachRow((row: any, rowNumber: number) => {
    row.eachCell((cell: any) => {
      if (cell.value && typeof cell.value === 'string') {
        let text = cell.value;
        
        text = text.replace(/W1_COMP_YEAR_CURR/g, currentYear.toString());
        text = text.replace(/W1_COMP_YEAR_PREV/g, previousYear.toString());
        text = text.replace(/W2_COMP_YEAR_CURR/g, currentYear.toString());
        text = text.replace(/W2_COMP_YEAR_PREV/g, previousYear.toString());
        
        if (text !== cell.value) {
          const savedStyle = {
            font: cell.font ? { ...cell.font } : undefined,
            alignment: cell.alignment ? { ...cell.alignment } : undefined,
            border: cell.border ? { ...cell.border } : undefined,
            fill: cell.fill ? { ...cell.fill } : undefined,
            numFmt: cell.numFmt,
          };
          
          cell.value = text;
          
          if (savedStyle.font) cell.font = savedStyle.font;
          if (savedStyle.alignment) cell.alignment = savedStyle.alignment;
          if (savedStyle.border) cell.border = savedStyle.border;
          if (savedStyle.fill) cell.fill = savedStyle.fill;
          if (savedStyle.numFmt) cell.numFmt = savedStyle.numFmt;
        }
      }
    });
  });

  if (hotelNames && hotelNames.length > 0) {
    let sourcesCell: any = null;
    
    worksheet.eachRow((row: any, rowNumber: number) => {
      row.eachCell((cell: any) => {
        if (cell.value && typeof cell.value === 'string' && cell.value.includes('Sources :')) {
          sourcesCell = cell;
        }
      });
    });
    
    if (sourcesCell) {
      const savedStyle = {
        font: sourcesCell.font ? { ...sourcesCell.font } : undefined,
        alignment: sourcesCell.alignment ? { ...sourcesCell.alignment } : undefined,
        border: sourcesCell.border ? { ...sourcesCell.border } : undefined,
        fill: sourcesCell.fill ? { ...sourcesCell.fill } : undefined,
        numFmt: sourcesCell.numFmt,
      };
      
      const hotelList = hotelNames.join(', ');
      sourcesCell.value = `Sources : ${hotelList}`;
      
      if (savedStyle.font) sourcesCell.font = savedStyle.font;
      if (savedStyle.alignment) sourcesCell.alignment = savedStyle.alignment;
      if (savedStyle.border) sourcesCell.border = savedStyle.border;
      if (savedStyle.fill) sourcesCell.fill = savedStyle.fill;
      if (savedStyle.numFmt) sourcesCell.numFmt = savedStyle.numFmt;
    }
  }

  const buffer2 = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer2], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "grilles_info.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function exportGrilleReelleXlsx(params: {
  templateUrl?: string;
  startDate: Date;
  endDate: Date;
  weeklyOccupancyData: any[];
  comparisonTableData: any;
  comparisonData: any;
  hotelNames?: string[];
}) {
  const { 
    templateUrl = "/GRILLES_INFO_TEMPLATE.xlsx", 
    startDate, 
    endDate,
    weeklyOccupancyData,
    comparisonTableData,
    comparisonData,
    hotelNames = [] 
  } = params;

  const buffer = await fetchTemplate(templateUrl).catch(async (e) => {
    return await fetchTemplate("GRILLES_INFO_TEMPLATE.xlsx");
  });
  
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const worksheetTemp = workbook.worksheets.find((ws: any) => 
    ws.name.toLowerCase().includes('réelle') || ws.name.toLowerCase().includes('reelle')
  );

  if (!worksheetTemp) {
    throw new Error("Onglet 'Grille réelle' non trouvé dans le template");
  }

  const worksheet = worksheetTemp;

  const cellMappingReelle: Record<string, string> = {
    "ACHAL_CURR_DESTINATION": "C30",
    "ACHAL_CURR_EXCURSION": "C29",
    "ACHAL_CURR_TOTAL": "C31",
    "ACHAL_PREV_DESTINATION": "F30",
    "ACHAL_PREV_EXCURSION": "F29",
    "ACHAL_PREV_TOTAL": "F31",
    "PREV_OCC_NIGHTS": "C9",
    "PREV_OCC_RATE": "C10",
    "REAL_CURR_NIGHTS": "C19",
    "REAL_CURR_RATE": "C20",
    "REAL_DATE_END": "F36",
    "REAL_DATE_START": "C36",
    "REAL_OCC_NIGHTS_CURR": "F9",
    "REAL_OCC_RATE_CURR": "F10",
    "REAL_PREV_NIGHTS": "F19",
    "REAL_PREV_RATE": "F20",
  };

  // Helper to write to named cell using mapping
  function writeToCell(name: string, value: any): boolean {
    try {
      const cellAddress = cellMappingReelle[name];
      if (!cellAddress) return false;

      const cell = worksheet.getCell(cellAddress);
      const existingStyle = {
        font: cell.font ? { ...cell.font } : undefined,
        alignment: cell.alignment ? { ...cell.alignment } : undefined,
        border: cell.border ? { ...cell.border } : undefined,
        fill: cell.fill ? { ...cell.fill } : undefined,
        numFmt: cell.numFmt,
      };
      
      cell.value = value;
      
      if (existingStyle.font) cell.font = existingStyle.font;
      if (existingStyle.alignment) cell.alignment = existingStyle.alignment;
      if (existingStyle.border) cell.border = existingStyle.border;
      if (existingStyle.fill) cell.fill = existingStyle.fill;
      if (existingStyle.numFmt) cell.numFmt = existingStyle.numFmt;
      
      return true;
    } catch (e) {
      return false;
    }
  }

  const formatDateISO = (date: Date) => date.toISOString().split('T')[0];
  writeToCell("REAL_DATE_START", formatDateISO(startDate));
  writeToCell("REAL_DATE_END", formatDateISO(endDate));

  // Section 1 : OCCUPATION PRÉVUE vs RÉELLE (année courante)
  const forecastTotal = comparisonData?.forecastData?.current || [];
  const prevNights = forecastTotal.find((d: any) => d.name === "Nuitées prévues")?.data || 0;
  const prevRateStr = forecastTotal.find((d: any) => d.name === "Taux prévu")?.data || "0%";
  const prevRate = prevRateStr.toString().replace('%', '');
  
  writeToCell("PREV_OCC_NIGHTS", prevNights);
  writeToCell("PREV_OCC_RATE", `${prevRate}%`);

  if (comparisonTableData) {
    writeToCell("REAL_OCC_NIGHTS_CURR", comparisonTableData.occupiedNights?.current || 0);
    writeToCell("REAL_OCC_RATE_CURR", `${comparisonTableData.averageOccupancy?.current || 0}%`);
  }

  // Section 2 : OCCUPATION RÉELLE année courante vs N-1
  if (comparisonTableData) {
    writeToCell("REAL_CURR_NIGHTS", comparisonTableData.occupiedNights?.current || 0);
    writeToCell("REAL_CURR_RATE", `${comparisonTableData.averageOccupancy?.current || 0}%`);
    writeToCell("REAL_PREV_NIGHTS", comparisonTableData.occupiedNights?.previous || 0);
    writeToCell("REAL_PREV_RATE", `${comparisonTableData.averageOccupancy?.previous || 0}%`);
  }

  // Section 3 : ACHALANDAGE (Visiteurs)
  const visitorsCurrent = comparisonData?.visitorData?.current || [];
  const visitorsPrevious = comparisonData?.visitorData?.previous || [];

  const currExcursion = visitorsCurrent.find((v: any) => v.name.toLowerCase().includes('excursion'))?.data || 'N/A';
  const currDestination = visitorsCurrent.find((v: any) => v.name.toLowerCase().includes('destination'))?.data || 0;
  const currTotal = visitorsCurrent.find((v: any) => v.name.toLowerCase().includes('total'))?.data || 0;

  writeToCell("ACHAL_CURR_EXCURSION", currExcursion);
  writeToCell("ACHAL_CURR_DESTINATION", currDestination);
  writeToCell("ACHAL_CURR_TOTAL", currTotal);

  const prevExcursion = visitorsPrevious.find((v: any) => v.name.toLowerCase().includes('excursion'))?.data || 'N/A';
  const prevDestination = visitorsPrevious.find((v: any) => v.name.toLowerCase().includes('destination'))?.data || 0;
  const prevTotal = visitorsPrevious.find((v: any) => v.name.toLowerCase().includes('total'))?.data || 0;

  writeToCell("ACHAL_PREV_EXCURSION", prevExcursion);
  writeToCell("ACHAL_PREV_DESTINATION", prevDestination);
  writeToCell("ACHAL_PREV_TOTAL", prevTotal);

  // Ajouter la liste des hôtels dans "Sources"
  if (hotelNames && hotelNames.length > 0) {
    let sourcesCell: any = null;
    
    worksheet.eachRow((row: any) => {
      row.eachCell((cell: any) => {
        if (cell.value && typeof cell.value === 'string' && cell.value.includes('Sources :')) {
          sourcesCell = cell;
        }
      });
    });
    
    if (sourcesCell) {
      const savedStyle = {
        font: sourcesCell.font ? { ...sourcesCell.font } : undefined,
        alignment: sourcesCell.alignment ? { ...sourcesCell.alignment } : undefined,
        border: sourcesCell.border ? { ...sourcesCell.border } : undefined,
        fill: sourcesCell.fill ? { ...sourcesCell.fill } : undefined,
        numFmt: sourcesCell.numFmt,
      };
      
      const hotelList = hotelNames.join(', ');
      sourcesCell.value = `Sources : ${hotelList}`;
      
      if (savedStyle.font) sourcesCell.font = savedStyle.font;
      if (savedStyle.alignment) sourcesCell.alignment = savedStyle.alignment;
      if (savedStyle.border) sourcesCell.border = savedStyle.border;
      if (savedStyle.fill) sourcesCell.fill = savedStyle.fill;
      if (savedStyle.numFmt) sourcesCell.numFmt = savedStyle.numFmt;
    }
  }

  const buffer2 = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer2], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "grille_reelle.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function exportGrillesCompletXlsx(params: {
  templateUrl?: string;
  startDate: Date;
  endDate: Date;
  weeklyForecastData: DailyForecast[];
  weeklyOccupancyData: any[];
  comparisonWeek1?: ComparisonData | null;
  comparisonWeek2?: ComparisonData | null;
  comparisonTableData: any;
  comparisonData: any;
  hotelNames?: string[];
}) {
  const { 
    templateUrl = "/GRILLES_INFO_TEMPLATE.xlsx", 
    startDate, 
    endDate,
    weeklyForecastData,
    weeklyOccupancyData,
    comparisonWeek1,
    comparisonWeek2,
    comparisonTableData,
    comparisonData,
    hotelNames = [] 
  } = params;

  const buffer = await fetchTemplate(templateUrl).catch(async (e) => {
    return await fetchTemplate("GRILLES_INFO_TEMPLATE.xlsx");
  });
  
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  // ========================================
  // ONGLET 1 : GRILLE PRÉVISION
  // ========================================
  const previsionSheetTemp = workbook.worksheets.find((ws: any) => 
    ws.name.toLowerCase().includes('prévision') || ws.name.toLowerCase().includes('prevision')
  );

  if (previsionSheetTemp) {
    const previsionSheet = previsionSheetTemp;
    
    const week1 = weeklyForecastData.slice(0, 7);
    const week2 = weeklyForecastData.slice(7, 14);
    const dayKeys = ["FRI", "SAT", "SUN", "MON", "TUE", "WED", "THU"] as const;

    const cellMappingPrevision: Record<string, string> = {
      "W1_AVG_AVAILABLE": "C8", "W1_COMP_AVAIL_CURR": "G10", "W1_COMP_AVAIL_DELTA": "H10", "W1_COMP_AVAIL_PREV": "F10",
      "W1_COMP_OCCUP_CURR": "G11", "W1_COMP_OCCUP_DELTA": "H11", "W1_COMP_OCCUP_PREV": "F11",
      "W1_COMP_PERS_CURR": "G12", "W1_COMP_PERS_DELTA": "H12", "W1_COMP_PERS_PREV": "F12",
      "W1_COMP_RATE_CURR": "G13", "W1_COMP_RATE_DELTA": "H13", "W1_COMP_RATE_PREV": "F13",
      "W1_COMP_YEAR_CURR": "G8", "W1_COMP_YEAR_PREV": "F8",
      "W1_DATES_FRI": "C2", "W1_DATES_MON": "F2", "W1_DATES_SAT": "D2", "W1_DATES_SUN": "E2",
      "W1_DATES_THU": "I2", "W1_DATES_TUE": "G2", "W1_DATES_WED": "H2",
      "W1_NIGHTS_FRI": "C4", "W1_NIGHTS_MON": "F4", "W1_NIGHTS_SAT": "D4", "W1_NIGHTS_SUN": "E4",
      "W1_NIGHTS_THU": "I4", "W1_NIGHTS_TOTAL": "J4", "W1_NIGHTS_TUE": "G4", "W1_NIGHTS_WED": "H4",
      "W1_OCC_AVG": "J6", "W1_OCC_FRI": "C6", "W1_OCC_MON": "F6", "W1_OCC_SAT": "D6",
      "W1_OCC_SUN": "E6", "W1_OCC_THU": "I6", "W1_OCC_TUE": "G6", "W1_OCC_WED": "H6",
      "W1_PERSONS_FRI": "C5", "W1_PERSONS_MON": "F5", "W1_PERSONS_SAT": "D5", "W1_PERSONS_SUN": "E5",
      "W1_PERSONS_THU": "I5", "W1_PERSONS_TOTAL": "J5", "W1_PERSONS_TUE": "G5", "W1_PERSONS_WED": "H5",
      "W1_RATIO_USED": "C7",
      "W2_AVG_AVAILABLE": "C21", "W2_COMP_AVAIL_CURR": "G23", "W2_COMP_AVAIL_DELTA": "H23", "W2_COMP_AVAIL_PREV": "F23",
      "W2_COMP_OCCUP_CURR": "G24", "W2_COMP_OCCUP_DELTA": "H24", "W2_COMP_OCCUP_PREV": "F24",
      "W2_COMP_PERS_CURR": "G25", "W2_COMP_PERS_DELTA": "H25", "W2_COMP_PERS_PREV": "F25",
      "W2_COMP_RATE_CURR": "G26", "W2_COMP_RATE_DELTA": "H26", "W2_COMP_RATE_PREV": "F26",
      "W2_COMP_YEAR_CURR": "G21", "W2_COMP_YEAR_PREV": "F21",
      "W2_DATES_FRI": "C15", "W2_DATES_MON": "F15", "W2_DATES_SAT": "D15", "W2_DATES_SUN": "E15",
      "W2_DATES_THU": "I15", "W2_DATES_TUE": "G15", "W2_DATES_WED": "H15",
      "W2_NIGHTS_FRI": "C17", "W2_NIGHTS_MON": "F17", "W2_NIGHTS_SAT": "D17", "W2_NIGHTS_SUN": "E17",
      "W2_NIGHTS_THU": "I17", "W2_NIGHTS_TOTAL": "J17", "W2_NIGHTS_TUE": "G17", "W2_NIGHTS_WED": "H17",
      "W2_OCC_AVG": "J19", "W2_OCC_FRI": "C19", "W2_OCC_MON": "F19", "W2_OCC_SAT": "D19",
      "W2_OCC_SUN": "E19", "W2_OCC_THU": "I19", "W2_OCC_TUE": "G19", "W2_OCC_WED": "H19",
      "W2_PERSONS_FRI": "C18", "W2_PERSONS_MON": "F18", "W2_PERSONS_SAT": "D18", "W2_PERSONS_SUN": "E18",
      "W2_PERSONS_THU": "I18", "W2_PERSONS_TOTAL": "J18", "W2_PERSONS_TUE": "G18", "W2_PERSONS_WED": "H18",
      "W2_RATIO_USED": "C20",
    };

    const writeToPrevisionCell = (name: string, value: any) => {
      const cellAddress = cellMappingPrevision[name];
      if (!cellAddress) return;
      const cell = previsionSheet.getCell(cellAddress);
      const savedStyle = { font: cell.font, alignment: cell.alignment, border: cell.border, fill: cell.fill, numFmt: cell.numFmt };
      cell.value = value;
      if (savedStyle.font) cell.font = savedStyle.font;
      if (savedStyle.alignment) cell.alignment = savedStyle.alignment;
      if (savedStyle.border) cell.border = savedStyle.border;
      if (savedStyle.fill) cell.fill = savedStyle.fill;
      if (savedStyle.numFmt) cell.numFmt = savedStyle.numFmt;
    };

    // Week 1 data
    dayKeys.forEach((dk, idx) => {
      const d = week1[idx] || { nights: 0, people: 0, occupancyRate: 0 };
      const dateLabel = formatDateExcelLabel(new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + idx));
      writeToPrevisionCell(`W1_DATES_${dk}`, dateLabel);
      writeToPrevisionCell(`W1_NIGHTS_${dk}`, d.nights || 0);
      writeToPrevisionCell(`W1_PERSONS_${dk}`, d.people || 0);
      writeToPrevisionCell(`W1_OCC_${dk}`, `${Number(d.occupancyRate || 0).toFixed(2)}%`);
    });

    const w1Nights = week1.map(d => d.nights || 0);
    const w1People = week1.map(d => d.people || 0);
    const w1Occ = week1.map(d => d.occupancyRate || 0);
    writeToPrevisionCell("W1_NIGHTS_TOTAL", sum(w1Nights));
    writeToPrevisionCell("W1_PERSONS_TOTAL", sum(w1People));
    writeToPrevisionCell("W1_OCC_AVG", `${(sum(w1Occ)/7).toFixed(2)}%`);
    const ratio1 = sum(w1Nights) > 0 ? (sum(w1People) / sum(w1Nights)) : 0;
    writeToPrevisionCell("W1_RATIO_USED", Number(ratio1.toFixed(2)));
    const availList1 = week1.map(d => d.occupancyRate > 0 ? Math.round(d.nights * 100 / d.occupancyRate) : 0);
    writeToPrevisionCell("W1_AVG_AVAILABLE", availList1.length ? Math.round(sum(availList1) / availList1.length) : 0);

    if (comparisonWeek1) {
      const currentYear = startDate.getFullYear();
      writeToPrevisionCell("W1_COMP_YEAR_PREV", currentYear - 1);
      writeToPrevisionCell("W1_COMP_YEAR_CURR", currentYear);
      writeToPrevisionCell("W1_COMP_AVAIL_PREV", comparisonWeek1.availableNights.previous);
      writeToPrevisionCell("W1_COMP_AVAIL_CURR", comparisonWeek1.availableNights.current);
      writeToPrevisionCell("W1_COMP_AVAIL_DELTA", comparisonWeek1.availableNights.previous === 0 ? "—" : `${(((comparisonWeek1.availableNights.current - comparisonWeek1.availableNights.previous) / comparisonWeek1.availableNights.previous) * 100).toFixed(1)}%`);
      writeToPrevisionCell("W1_COMP_OCCUP_PREV", comparisonWeek1.occupiedNights.previous);
      writeToPrevisionCell("W1_COMP_OCCUP_CURR", comparisonWeek1.occupiedNights.current);
      writeToPrevisionCell("W1_COMP_OCCUP_DELTA", comparisonWeek1.occupiedNights.previous === 0 ? "—" : `${(((comparisonWeek1.occupiedNights.current - comparisonWeek1.occupiedNights.previous) / comparisonWeek1.occupiedNights.previous) * 100).toFixed(1)}%`);
      writeToPrevisionCell("W1_COMP_PERS_PREV", comparisonWeek1.persons.previous);
      writeToPrevisionCell("W1_COMP_PERS_CURR", comparisonWeek1.persons.current);
      writeToPrevisionCell("W1_COMP_PERS_DELTA", comparisonWeek1.persons.previous === 0 ? "—" : `${(((comparisonWeek1.persons.current - comparisonWeek1.persons.previous) / comparisonWeek1.persons.previous) * 100).toFixed(1)}%`);
      writeToPrevisionCell("W1_COMP_RATE_PREV", `${comparisonWeek1.averageOccupancy.previous}%`);
      writeToPrevisionCell("W1_COMP_RATE_CURR", `${comparisonWeek1.averageOccupancy.current}%`);
      writeToPrevisionCell("W1_COMP_RATE_DELTA", comparisonWeek1.averageOccupancy.previous === 0 ? "—" : `${(((comparisonWeek1.averageOccupancy.current - comparisonWeek1.averageOccupancy.previous) / comparisonWeek1.averageOccupancy.previous) * 100).toFixed(1)}%`);
    }

    // Week 2 data
    dayKeys.forEach((dk, idx) => {
      const d = week2[idx] || { nights: 0, people: 0, occupancyRate: 0 };
      const dateLabel = formatDateExcelLabel(new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 7 + idx));
      writeToPrevisionCell(`W2_DATES_${dk}`, dateLabel);
      writeToPrevisionCell(`W2_NIGHTS_${dk}`, d.nights || 0);
      writeToPrevisionCell(`W2_PERSONS_${dk}`, d.people || 0);
      writeToPrevisionCell(`W2_OCC_${dk}`, `${Number(d.occupancyRate || 0).toFixed(2)}%`);
    });

    const w2Nights = week2.map(d => d.nights || 0);
    const w2People = week2.map(d => d.people || 0);
    const w2Occ = week2.map(d => d.occupancyRate || 0);
    writeToPrevisionCell("W2_NIGHTS_TOTAL", sum(w2Nights));
    writeToPrevisionCell("W2_PERSONS_TOTAL", sum(w2People));
    writeToPrevisionCell("W2_OCC_AVG", `${(sum(w2Occ)/7).toFixed(2)}%`);
    const ratio2 = sum(w2Nights) > 0 ? (sum(w2People) / sum(w2Nights)) : 0;
    writeToPrevisionCell("W2_RATIO_USED", Number(ratio2.toFixed(2)));
    const availList2 = week2.map(d => d.occupancyRate > 0 ? Math.round(d.nights * 100 / d.occupancyRate) : 0);
    writeToPrevisionCell("W2_AVG_AVAILABLE", availList2.length ? Math.round(sum(availList2) / availList2.length) : 0);

    if (comparisonWeek2) {
      const currentYear = startDate.getFullYear();
      writeToPrevisionCell("W2_COMP_YEAR_PREV", currentYear - 1);
      writeToPrevisionCell("W2_COMP_YEAR_CURR", currentYear);
      writeToPrevisionCell("W2_COMP_AVAIL_PREV", comparisonWeek2.availableNights.previous);
      writeToPrevisionCell("W2_COMP_AVAIL_CURR", comparisonWeek2.availableNights.current);
      writeToPrevisionCell("W2_COMP_AVAIL_DELTA", comparisonWeek2.availableNights.previous === 0 ? "—" : `${(((comparisonWeek2.availableNights.current - comparisonWeek2.availableNights.previous) / comparisonWeek2.availableNights.previous) * 100).toFixed(1)}%`);
      writeToPrevisionCell("W2_COMP_OCCUP_PREV", comparisonWeek2.occupiedNights.previous);
      writeToPrevisionCell("W2_COMP_OCCUP_CURR", comparisonWeek2.occupiedNights.current);
      writeToPrevisionCell("W2_COMP_OCCUP_DELTA", comparisonWeek2.occupiedNights.previous === 0 ? "—" : `${(((comparisonWeek2.occupiedNights.current - comparisonWeek2.occupiedNights.previous) / comparisonWeek2.occupiedNights.previous) * 100).toFixed(1)}%`);
      writeToPrevisionCell("W2_COMP_PERS_PREV", comparisonWeek2.persons.previous);
      writeToPrevisionCell("W2_COMP_PERS_CURR", comparisonWeek2.persons.current);
      writeToPrevisionCell("W2_COMP_PERS_DELTA", comparisonWeek2.persons.previous === 0 ? "—" : `${(((comparisonWeek2.persons.current - comparisonWeek2.persons.previous) / comparisonWeek2.persons.previous) * 100).toFixed(1)}%`);
      writeToPrevisionCell("W2_COMP_RATE_PREV", `${comparisonWeek2.averageOccupancy.previous}%`);
      writeToPrevisionCell("W2_COMP_RATE_CURR", `${comparisonWeek2.averageOccupancy.current}%`);
      writeToPrevisionCell("W2_COMP_RATE_DELTA", comparisonWeek2.averageOccupancy.previous === 0 ? "—" : `${(((comparisonWeek2.averageOccupancy.current - comparisonWeek2.averageOccupancy.previous) / comparisonWeek2.averageOccupancy.previous) * 100).toFixed(1)}%`);
    }

    // Remplacer années dans textes
    const currentYear = startDate.getFullYear();
    const previousYear = currentYear - 1;
    previsionSheet.eachRow((row: any) => {
      row.eachCell((cell: any) => {
        if (cell.value && typeof cell.value === 'string') {
          let text = cell.value;
          text = text.replace(/W1_COMP_YEAR_CURR/g, currentYear.toString());
          text = text.replace(/W1_COMP_YEAR_PREV/g, previousYear.toString());
          text = text.replace(/W2_COMP_YEAR_CURR/g, currentYear.toString());
          text = text.replace(/W2_COMP_YEAR_PREV/g, previousYear.toString());
          if (text !== cell.value) {
            const savedStyle = { font: cell.font, alignment: cell.alignment, border: cell.border, fill: cell.fill, numFmt: cell.numFmt };
            cell.value = text;
            if (savedStyle.font) cell.font = savedStyle.font;
            if (savedStyle.alignment) cell.alignment = savedStyle.alignment;
            if (savedStyle.border) cell.border = savedStyle.border;
            if (savedStyle.fill) cell.fill = savedStyle.fill;
            if (savedStyle.numFmt) cell.numFmt = savedStyle.numFmt;
          }
        }
      });
    });

    // Sources
    if (hotelNames.length > 0) {
      previsionSheet.eachRow((row: any) => {
        row.eachCell((cell: any) => {
          if (cell.value && typeof cell.value === 'string' && cell.value.includes('Sources :')) {
            const savedStyle = { font: cell.font, alignment: cell.alignment, border: cell.border, fill: cell.fill, numFmt: cell.numFmt };
            cell.value = `Sources : ${hotelNames.join(', ')}`;
            if (savedStyle.font) cell.font = savedStyle.font;
            if (savedStyle.alignment) cell.alignment = savedStyle.alignment;
            if (savedStyle.border) cell.border = savedStyle.border;
            if (savedStyle.fill) cell.fill = savedStyle.fill;
            if (savedStyle.numFmt) cell.numFmt = savedStyle.numFmt;
          }
        });
      });
    }
  }

  // ========================================
  // ONGLET 2 : GRILLE RÉELLE
  // ========================================
  const reelleSheetTemp = workbook.worksheets.find((ws: any) => 
    ws.name.toLowerCase().includes('réelle') || ws.name.toLowerCase().includes('reelle')
  );

  if (reelleSheetTemp) {
    const reelleSheet = reelleSheetTemp;
    console.log("[grillesXlsx] Remplissage onglet Réelle:", reelleSheet.name);

    const cellMappingReelle: Record<string, string> = {
      "ACHAL_CURR_DESTINATION": "C30", "ACHAL_CURR_EXCURSION": "C29", "ACHAL_CURR_TOTAL": "C31",
      "ACHAL_PREV_DESTINATION": "F30", "ACHAL_PREV_EXCURSION": "F29", "ACHAL_PREV_TOTAL": "F31",
      "PREV_OCC_NIGHTS": "C9", "PREV_OCC_RATE": "C10",
      "REAL_CURR_NIGHTS": "C19", "REAL_CURR_RATE": "C20",
      "REAL_DATE_END": "F36", "REAL_DATE_START": "C36",
      "REAL_OCC_NIGHTS_CURR": "F9", "REAL_OCC_RATE_CURR": "F10",
      "REAL_PREV_NIGHTS": "F19", "REAL_PREV_RATE": "F20",
    };

    const writeToReelleCell = (name: string, value: any) => {
      const cellAddress = cellMappingReelle[name];
      if (!cellAddress) return;
      const cell = reelleSheet.getCell(cellAddress);
      const savedStyle = { font: cell.font, alignment: cell.alignment, border: cell.border, fill: cell.fill, numFmt: cell.numFmt };
      cell.value = value;
      if (savedStyle.font) cell.font = savedStyle.font;
      if (savedStyle.alignment) cell.alignment = savedStyle.alignment;
      if (savedStyle.border) cell.border = savedStyle.border;
      if (savedStyle.fill) cell.fill = savedStyle.fill;
      if (savedStyle.numFmt) cell.numFmt = savedStyle.numFmt;
    };

    // Dates
    const formatDateISO = (date: Date) => date.toISOString().split('T')[0];
    writeToReelleCell("REAL_DATE_START", formatDateISO(startDate));
    writeToReelleCell("REAL_DATE_END", formatDateISO(endDate));

    // Section 1
    const forecastTotal = comparisonData?.forecastData?.current || [];
    const prevNights = forecastTotal.find((d: any) => d.name === "Nuitées prévues")?.data || 0;
    const prevRateStr = forecastTotal.find((d: any) => d.name === "Taux prévu")?.data || "0%";
    const prevRate = prevRateStr.toString().replace('%', '');
    writeToReelleCell("PREV_OCC_NIGHTS", prevNights);
    writeToReelleCell("PREV_OCC_RATE", `${prevRate}%`);

    if (comparisonTableData) {
      writeToReelleCell("REAL_OCC_NIGHTS_CURR", comparisonTableData.occupiedNights?.current || 0);
      writeToReelleCell("REAL_OCC_RATE_CURR", `${comparisonTableData.averageOccupancy?.current || 0}%`);
      writeToReelleCell("REAL_CURR_NIGHTS", comparisonTableData.occupiedNights?.current || 0);
      writeToReelleCell("REAL_CURR_RATE", `${comparisonTableData.averageOccupancy?.current || 0}%`);
      writeToReelleCell("REAL_PREV_NIGHTS", comparisonTableData.occupiedNights?.previous || 0);
      writeToReelleCell("REAL_PREV_RATE", `${comparisonTableData.averageOccupancy?.previous || 0}%`);
    }

    // Section 3
    const visitorsCurrent = comparisonData?.visitorData?.current || [];
    const visitorsPrevious = comparisonData?.visitorData?.previous || [];
    writeToReelleCell("ACHAL_CURR_EXCURSION", visitorsCurrent.find((v: any) => v.name.toLowerCase().includes('excursion'))?.data || 'N/A');
    writeToReelleCell("ACHAL_CURR_DESTINATION", visitorsCurrent.find((v: any) => v.name.toLowerCase().includes('destination'))?.data || 0);
    writeToReelleCell("ACHAL_CURR_TOTAL", visitorsCurrent.find((v: any) => v.name.toLowerCase().includes('total'))?.data || 0);
    writeToReelleCell("ACHAL_PREV_EXCURSION", visitorsPrevious.find((v: any) => v.name.toLowerCase().includes('excursion'))?.data || 'N/A');
    writeToReelleCell("ACHAL_PREV_DESTINATION", visitorsPrevious.find((v: any) => v.name.toLowerCase().includes('destination'))?.data || 0);
    writeToReelleCell("ACHAL_PREV_TOTAL", visitorsPrevious.find((v: any) => v.name.toLowerCase().includes('total'))?.data || 0);

    // Sources
    if (hotelNames.length > 0) {
      reelleSheet.eachRow((row: any) => {
        row.eachCell((cell: any) => {
          if (cell.value && typeof cell.value === 'string' && cell.value.includes('Sources :')) {
            const savedStyle = { font: cell.font, alignment: cell.alignment, border: cell.border, fill: cell.fill, numFmt: cell.numFmt };
            cell.value = `Sources : ${hotelNames.join(', ')}`;
            if (savedStyle.font) cell.font = savedStyle.font;
            if (savedStyle.alignment) cell.alignment = savedStyle.alignment;
            if (savedStyle.border) cell.border = savedStyle.border;
            if (savedStyle.fill) cell.fill = savedStyle.fill;
            if (savedStyle.numFmt) cell.numFmt = savedStyle.numFmt;
          }
        });
      });
    }
  }

  const buffer2 = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer2], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  
  const dateStr = startDate.toISOString().split('T')[0];
  a.download = `grilles_info_${dateStr}.xlsx`;
  
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
